import React, { memo, useMemo } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { LEAVE_CALENDAR_CELL_WIDTH } from "./leaveCalendar.constants";
import { dayKey, isToday, leaveDaysInMonth, leaveIntersectsMonth } from "./leaveCalendarDates";
import {
  LEAVE_TYPE_BG as CELL_BG,
  LEAVE_TYPE_BORDER as CELL_BORDER,
  LEAVE_TYPE_TEXT as CELL_TEXT,
  LEAVE_TYPE_LABEL as CELL_LABEL,
} from "./leaveCalendarColors";
import type {
  CalendarUser,
  LeaveRecord,
  PublicHoliday,
} from "./leaveCalendar.types";

export interface LeaveCalendarUserRowProps {
  user: CalendarUser;
  days: Date[];
  monthStart: Date;
  monthEnd: Date;
  leaves: LeaveRecord[];
  holidays: PublicHoliday[];
  cellWidth?: number;
  dragPreview: { startIdx: number; endIdx: number } | null;
  onRowPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  onLeaveClick: (leave: LeaveRecord) => void;
  saturdayOff?: boolean;
  sundayOff?: boolean;
  /** Tooltip label for fixed leave codes (SL, CL, …). */
  resolveLeaveTypeLabel?: (code: string) => string;
  /** Leave id to emphasize when opening a shared calendar link. */
  highlightedLeaveId?: string | null;
}

/* ── component ─────────────────────────────────────────────────────── */

export const LeaveCalendarUserRow = memo(function LeaveCalendarUserRow({
  user,
  days,
  monthStart,
  monthEnd,
  leaves,
  holidays,
  cellWidth = LEAVE_CALENDAR_CELL_WIDTH,
  dragPreview,
  onRowPointerDown,
  onLeaveClick,
  saturdayOff = true,
  sundayOff = true,
  resolveLeaveTypeLabel,
  highlightedLeaveId,
}: LeaveCalendarUserRowProps) {
  const { t } = useTranslation();
  const w = cellWidth;

  /* build day → LeaveRecord lookup for this user */
  const dayLeaveMap = useMemo(() => {
    const map = new Map<string, LeaveRecord>();
    const userLeaves = leaves.filter(
      (l) =>
        l.userId === user.id &&
        leaveIntersectsMonth(l.startDate, l.endDate, monthStart, monthEnd)
    );
    for (const leave of userLeaves) {
      const interval = leaveDaysInMonth(
        leave.startDate,
        leave.endDate,
        monthStart,
        monthEnd
      );
      for (const d of interval) {
        const key = dayKey(d);
        const day = d.getDay();
        const weekend = (day === 6 && saturdayOff) || (day === 0 && sundayOff);
        const hol = holidays.some(h => h.date === key);
        if (weekend || hol) continue;

        if (!map.has(key)) {
          map.set(key, leave);
        }
      }
    }
    return map;
  }, [leaves, user.id, monthStart, monthEnd, saturdayOff, sundayOff, holidays]);

  const holidayKeys = useMemo(
    () => new Set(holidays.map((h) => h.date)),
    [holidays]
  );

  const dragStart = dragPreview
    ? Math.min(dragPreview.startIdx, dragPreview.endIdx)
    : -1;
  const dragEnd = dragPreview
    ? Math.max(dragPreview.startIdx, dragPreview.endIdx)
    : -1;

  return (
    <div
      className="flex select-none border-b border-slate-200/70 bg-white"
      style={{
        height: "100%",
        width: days.length * w,
        display: "grid",
        gridTemplateColumns: `repeat(${days.length}, ${w}px)`,
      }}
      onPointerDown={onRowPointerDown}
    >
      {days.map((d, i) => {
        const day = d.getDay();
        const weekend = (day === 6 && saturdayOff) || (day === 0 && sundayOff);
        const key = dayKey(d);
        const hol = holidayKeys.has(key);
        const today = isToday(d);
        const leave = dayLeaveMap.get(key);
        const inDrag = dragPreview && i >= dragStart && i <= dragEnd;

        const typeTitle =
          leave &&
          (resolveLeaveTypeLabel?.(leave.leaveType) ||
              t(`leave_calendar_type_${leave.type}`));
        const title = leave
          ? [
              typeTitle,
              `${leave.startDate} → ${leave.endDate}`,
              (leave.duration === "half_day" || leave.duration === "hourly") &&
              leave.startTime &&
              leave.endTime
                ? `${leave.startTime}–${leave.endTime}`
                : undefined,
              leave.status === "pending_approval"
                ? t("leave_calendar_status_pending_approval")
                : leave.status === "rejected"
                ? t("leave_calendar_status_rejected")
                : leave.status === "cancelled"
                ? t("leave_calendar_status_cancelled")
                : t("leave_calendar_status_approved"),
              leave.comment,
            ]
              .filter(Boolean)
              .join(" · ")
          : undefined;

        const pending = leave?.status === "pending_approval";

        return (
          <div
            key={key}
            className={clsx(
              "flex flex-col border-r border-slate-100/60 transition-colors p-[3px]",
              !leave && "items-center justify-center p-0",
              /* leave cell */
              leave && "cursor-pointer",
              pending && leave && "ring-1 ring-inset ring-amber-400/50 rounded-lg",
              leave?.id === highlightedLeaveId &&
                "ring-2 ring-indigo-500/80 shadow-lg scale-105 z-[2] rounded-lg",
              /* empty-cell tints */
              !leave &&
                today &&
                "bg-teal-50/20 ring-1 ring-inset ring-teal-500/30 z-[1]",
              /* drag preview on empty cells */
              inDrag &&
                !leave &&
                "bg-indigo-400/10 ring-1 ring-inset ring-indigo-400/40 rounded-md"
            )}
            style={{
              width: w,
              height: "100%",
              boxSizing: "border-box",
              ...(!leave && !today && weekend && !hol
                ? {
                    background:
                      "repeating-linear-gradient(-45deg, #fafafa, #fafafa 4px, #f1f5f9 4px, #f1f5f9 8px)",
                  }
                : {}),
              ...(!leave && !today && hol
                ? {
                    background:
                      "repeating-linear-gradient(-45deg, #fffbeb, #fffbeb 4px, #fef3c7 4px, #fef3c7 8px)",
                  }
                : {}),
            }}
            title={title}
            {...(leave
              ? {
                  role: "button",
                  tabIndex: -1,
                  "data-leave-segment": true,
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    onLeaveClick(leave);
                  },
                  onPointerDown: (e: React.PointerEvent) => {
                    e.stopPropagation();
                  },
                }
              : {})}
          >
            {leave && (
              <>
                {leave.duration === "half_day" && (
                  <div className="min-h-[50%] shrink-0 bg-transparent" />
                )}
                {leave.duration === "hourly" && (
                  <div className="min-h-[75%] shrink-0 bg-transparent" />
                )}
                <div
                  className={clsx(
                    "flex flex-1 items-center justify-center rounded-md border transition-all duration-150 hover:scale-[1.03] hover:shadow-md",
                    leave.duration === "half_day" ? "min-h-[50%]" : 
                    leave.duration === "hourly" ? "min-h-[25%]" : "h-full",
                    (leave.status === "rejected" || leave.status === "cancelled")
                      ? "bg-rose-500/85 backdrop-blur-sm border-rose-300/30 text-white shadow-sm shadow-red-100"
                      : leave.status === "pending_approval"
                      ? "bg-amber-400/85 backdrop-blur-sm border-amber-300/35 text-slate-800 shadow-sm shadow-amber-100"
                      : `${CELL_BG[leave.type]} ${CELL_BORDER[leave.type]}`
                  )}
                >
                  <span
                    className={clsx(
                      "text-[11px] font-extrabold select-none tracking-wide lg:text-[10px]",
                      (leave.status === "rejected" || leave.status === "cancelled")
                        ? "text-white"
                        : leave.status === "pending_approval"
                        ? "text-slate-800"
                        : CELL_TEXT[leave.type]
                    )}
                  >
                    {CELL_LABEL[leave.type]}
                  </span>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
});
